import { Calendar } from 'components/Calendar/Calendar'
import { forwardRef } from 'react'
import Styles from './DateTimePicker.module.scss'
import dayjs, { Dayjs } from 'dayjs'
import TimePicker, { NativeTimePicker } from 'blaze-ui/TimePicker/TimePicker'
import { Time } from '@internationalized/date'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { TimeValue } from '@react-aria/datepicker'
import { isFunction, noop } from 'lodash'
import cx from 'classnames'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Text } from 'blaze-ui/Text/Text'
import { Placement } from '@floating-ui/react-dom'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
interface CalendarTimePickerProps {
  dateTime?: Dayjs
  setDateTime: (date: Dayjs | undefined) => void
  onClose?: () => void
  responsive?: boolean
  actions?: React.ReactNode | ((close: () => void) => React.ReactNode)
  headerActions?: React.ReactNode | ((close: () => void) => React.ReactNode)
  popoverClassName?: string
  submitOnDateChange?: boolean
  showTimeControls?: boolean
  allowPastDates?: boolean
  disableDatesBefore?: string
  disableDatesAfter?: string
  isDateDisabled?: boolean
  isAPMobileWeb?: boolean
  datesWithEvents?: Dayjs[]
  showClearDate?: boolean
  defaultFocusedValue?: string
}

export const timeFromDateTime = (dateTime: Dayjs): Time => {
  return new Time(dateTime.hour(), dateTime.minute())
}

const CalendarTimePicker = ({
  headerActions,
  dateTime,
  setDateTime,
  actions,
  onClose,
  responsive = false,
  popoverClassName,
  showTimeControls = true,
  allowPastDates = false,
  isDateDisabled = false,
  isAPMobileWeb = false,
  disableDatesBefore,
  disableDatesAfter,
  datesWithEvents = [],
  showClearDate = false,
  defaultFocusedValue,
}: CalendarTimePickerProps) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()

  const handleDateChange = (newDate: Dayjs) => {
    if (dateTime) {
      const updatedDateTime = dateTime.date(newDate.date()).month(newDate.month()).year(newDate.year())
      setDateTime(updatedDateTime)
    } else {
      setDateTime(newDate)
    }
  }

  const { isMobile } = useResponsiveDesign()

  const handleTimeChange = (newTime: TimeValue | null) => {
    if (!dateTime || !newTime) return
    const nextDateTime = dateTime.hour(newTime.hour).minute(newTime.minute)
    setDateTime(nextDateTime)
  }

  return (
    <div className={cx(Styles.popover, popoverClassName, responsive && Styles.responsive)}>
      {headerActions && (
        <div className={Styles.headerAction}>
          {isFunction(headerActions) ? headerActions(onClose || noop) : headerActions}
        </div>
      )}
      <Calendar
        responsive={responsive}
        isAPMobileWeb={isAPMobileWeb}
        isDateUnavailable={(date: string) => {
          const dateObj = dayjs(date)
          if (disableDatesBefore && dateObj.isBefore(dayjs(disableDatesBefore), 'day')) return true
          if (disableDatesAfter && dateObj.isAfter(dayjs(disableDatesAfter), 'day')) return true
          if (allowPastDates) return false

          return dateObj.isBefore(dayjs(), 'day')
        }}
        headerSize={isSystemRedesignEnabled ? 'md' : 'lg'}
        value={dateTime?.format('YYYY-MM-DD')}
        onChange={(newDate) => {
          if (newDate) {
            handleDateChange(dayjs(newDate))
          } else if (!!showClearDate) {
            setDateTime(undefined)
          }
        }}
        data-testid="select-date-button"
        isReadOnly={isDateDisabled}
        datesWithEvents={datesWithEvents}
        showClearDate={showClearDate}
        defaultFocusedValue={defaultFocusedValue}
      />
      {showTimeControls && (
        <div className={Styles.bottomSection}>
          <div className={Styles.dateTimeActions}>
            {isAPMobileWeb ? null : (
              <div className={Styles.dateSection}>
                <Text variant="label" color="var(--dark-40)">
                  Selected date
                </Text>
                {isSystemRedesignEnabled ? (
                  <div className={Styles.dateValue}>
                    <Text variant="secondary" color="var(--dark-80)">
                      {dateTime ? dateTime.format('ddd, MMM D') : 'No date set'}
                    </Text>
                  </div>
                ) : (
                  <Heading level={5} data-testid="date-time-value">
                    {dateTime ? dateTime.format('ddd, MMM D YYYY') : 'No date set'}
                  </Heading>
                )}
              </div>
            )}
            <div className={cx(Styles.timePicker, { [Styles.apMobileWeb]: isAPMobileWeb })}>
              {isMobile && (
                <Text variant="placeholder" color={'var(--dark-60)'}>
                  Time
                </Text>
              )}
              {isMobile ? (
                <NativeTimePicker
                  onChange={handleTimeChange}
                  value={dateTime ? timeFromDateTime(dateTime) : undefined}
                  showTimezone={isAPMobileWeb}
                  isAPMobileWeb={isAPMobileWeb}
                />
              ) : (
                <TimePicker
                  size={isSystemRedesignEnabled ? 'md' : 'lg'}
                  fullWidth={true}
                  value={dateTime ? timeFromDateTime(dateTime) : undefined}
                  onChange={handleTimeChange}
                  label="Time of day"
                  labelVariant="label"
                  labelColor="var(--dark-40)"
                  withIcon={!isSystemRedesignEnabled}
                />
              )}
            </div>
          </div>
          {isFunction(actions) ? actions(onClose || noop) : actions}
        </div>
      )}
    </div>
  )
}

interface DateTimePickerProps {
  wrapperClassName?: string
  popoverClassName?: string
  dateTime?: Dayjs
  setDateTime: (date: Dayjs | undefined) => void
  children?: React.ReactNode
  onClose?: () => void
  inline?: boolean
  responsive?: boolean
  actions?: React.ReactNode | ((close: () => void) => React.ReactNode)
  headerActions?: React.ReactNode | ((close: () => void) => React.ReactNode)
  showTimeControls?: boolean
  allowPastDates?: boolean
  disableDatesBefore?: string
  disableDatesAfter?: string
  isDateDisabled?: boolean
  placement?: Placement
  isOpen?: boolean
  onOpenChange?: (isOpen: boolean) => void
  useSqueeze?: boolean
  isAPMobileWeb?: boolean
  datesWithEvents?: Dayjs[]
  showClearDate?: boolean
  defaultFocusedValue?: string
}

export const DateTimePicker = forwardRef<{}, DateTimePickerProps>(
  (
    {
      wrapperClassName,
      popoverClassName,
      dateTime,
      setDateTime,
      children,
      onClose,
      inline,
      actions,
      headerActions,
      responsive = false,
      showTimeControls = true,
      allowPastDates = false,
      isDateDisabled = false,
      disableDatesBefore,
      disableDatesAfter,
      placement,
      isOpen,
      onOpenChange,
      useSqueeze = true,
      isAPMobileWeb = false,
      datesWithEvents = [],
      showClearDate = false,
      defaultFocusedValue,
    },
    ref,
  ) => {
    if (inline) {
      return (
        <div className={cx(wrapperClassName, Styles.wrapper)} data-testid="send-time-picker">
          <CalendarTimePicker
            headerActions={headerActions}
            responsive={responsive}
            dateTime={dateTime}
            setDateTime={setDateTime}
            actions={actions}
            showTimeControls={showTimeControls}
            allowPastDates={allowPastDates}
            isDateDisabled={isDateDisabled}
            disableDatesBefore={disableDatesBefore}
            disableDatesAfter={disableDatesAfter}
            isAPMobileWeb={isAPMobileWeb}
            datesWithEvents={datesWithEvents}
            showClearDate={showClearDate}
            defaultFocusedValue={defaultFocusedValue}
          />
        </div>
      )
    }

    return (
      <div className={wrapperClassName} data-testid="send-time-picker" onClick={(e) => e.stopPropagation()}>
        <DropdownTrigger
          shouldCloseOnBlur={false}
          disableCloseOnInteractOutside={false}
          onClose={onClose}
          placement={placement}
          forceOpen={isOpen}
          onOpenChange={onOpenChange}
          useSqueeze={useSqueeze}
          shiftOptions={{ padding: 8, crossAxis: true }}
        >
          {children}
          {(close) => (
            <div className={Styles.wrapper} data-testid="send-time-picker">
              <CalendarTimePicker
                dateTime={dateTime}
                setDateTime={setDateTime}
                onClose={close}
                responsive={responsive}
                actions={actions}
                headerActions={headerActions}
                popoverClassName={popoverClassName}
                showTimeControls={showTimeControls}
                allowPastDates={allowPastDates}
                disableDatesBefore={disableDatesBefore}
                disableDatesAfter={disableDatesAfter}
                isDateDisabled={isDateDisabled}
                isAPMobileWeb={isAPMobileWeb}
                datesWithEvents={datesWithEvents}
                showClearDate={showClearDate}
                defaultFocusedValue={defaultFocusedValue}
              />
            </div>
          )}
        </DropdownTrigger>
      </div>
    )
  },
)
DateTimePicker.displayName = 'DateTimePicker'
